import type { Mailbox } from '../api/types';

/*
    Folder identity and shape.

    Special folders are found by their JMAP `role` first and by name only as a
    fallback. The fallback is not cosmetic: this server's "Archive" carries
    role null, so every client that looks the role up — ours included — decided
    the mailbox had no archive at all and quietly disabled the button.

    Matching by name is the workaround; `unstamped` is the actual fix. It lists
    the folders we recognised by name that the server has no role on, so the UI
    can offer to write the role once and have every client agree from then on.
*/

export type Role = 'inbox' | 'drafts' | 'sent' | 'archive' | 'junk' | 'trash';

export const ROLE_ORDER: Role[] = ['inbox', 'drafts', 'sent', 'archive', 'junk', 'trash'];

/*
    Names servers and importers actually produce, Turkish and English. Yandex
    wrote "Sent Items", "Junk Mail" and "Deleted Items"; a Turkish Outlook writes
    "Gönderilmiş Öğeler". Both spellings of every Turkish word are listed because
    the normaliser also folds diacritics, so "Arşiv" and "Arsiv" both land here.
*/
const NAMES: Record<Role, string[]> = {
    inbox: ['inbox', 'gelen kutusu', 'gelen'],
    drafts: ['drafts', 'draft', 'taslaklar', 'taslak'],
    sent: [
        'sent', 'sent items', 'sent mail', 'sent messages', 'outbox',
        'gonderilmis', 'gonderilenler', 'gonderilmis ogeler', 'giden', 'giden kutusu',
    ],
    archive: ['archive', 'archives', 'arsiv', 'arsivler'],
    junk: [
        'junk', 'junk mail', 'junk e mail', 'spam', 'bulk mail',
        'gereksiz', 'onemsiz', 'istenmeyen', 'istenmeyen posta',
    ],
    trash: [
        'trash', 'bin', 'deleted', 'deleted items', 'deleted messages',
        'cop', 'cop kutusu', 'silinmis', 'silinmis ogeler', 'silinenler',
    ],
};

/*
    Turkish dotted capital İ becomes i + a combining dot under toLowerCase, so it
    is folded by hand first. The rest strips the diacritics that separate an
    otherwise identical Turkish and English folder name.
*/
const FOLD: Record<string, string> = {
    'ı': 'i', 'ş': 's', 'ğ': 'g', 'ö': 'o', 'ü': 'u', 'ç': 'c',
    'â': 'a', 'î': 'i', 'û': 'u', 'é': 'e', 'ñ': 'n',
};

export function normaliseName(name: string): string {
    return name
        .replace(/İ/g, 'i')
        .toLowerCase()
        .replace(/[ışğöüçâîûéñ]/g, (c) => FOLD[c] ?? c)
        .replace(/[_\-.]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** The role this folder's name implies, or null when the name tells us nothing. */
export function roleFromName(name: string): Role | null {
    const n = normaliseName(name);
    for (const role of ROLE_ORDER) {
        if (NAMES[role].includes(n)) return role;
    }
    return null;
}

export interface SpecialFolders {
    byRole: Partial<Record<Role, Mailbox>>;
    /** Recognised by name, but the server carries no role. Offer to stamp these. */
    unstamped: { role: Role; mailbox: Mailbox }[];
}

export function resolveSpecial(mailboxes: Mailbox[]): SpecialFolders {
    const byRole: Partial<Record<Role, Mailbox>> = {};
    const unstamped: { role: Role; mailbox: Mailbox }[] = [];

    for (const m of mailboxes) {
        const role = m.role as Role | null;
        if (role && ROLE_ORDER.includes(role)) byRole[role] ??= m;
    }

    // Only fill gaps. A real role always beats a name that happens to look right.
    for (const m of mailboxes) {
        if (m.role) continue;
        const guess = roleFromName(m.name);
        if (!guess || byRole[guess]) continue;
        byRole[guess] = m;
        unstamped.push({ role: guess, mailbox: m });
    }

    return { byRole, unstamped };
}

export interface FolderNode {
    mailbox: Mailbox;
    depth: number;
    children: FolderNode[];
}

const roleRank = (m: Mailbox): number => {
    const role = (m.role as Role | null) ?? roleFromName(m.name);
    const at = role ? ROLE_ORDER.indexOf(role) : -1;
    return at === -1 ? 99 : at;
};

function compare(a: Mailbox, b: Mailbox): number {
    const ra = roleRank(a);
    const rb = roleRank(b);
    if (ra !== rb) return ra - rb;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

/**
 * Builds the folder tree from `parentId`.
 *
 * A child whose parent is missing from the list — unsubscribed and hidden, or
 * simply not returned — is promoted to the top level rather than dropped. A
 * folder that exists but cannot be reached is worse than one shown in the wrong
 * place, because there is no way to discover it and no way to fix it.
 */
export function buildTree(mailboxes: Mailbox[]): FolderNode[] {
    const present = new Set(mailboxes.map((m) => m.id));
    const children = new Map<string, Mailbox[]>();
    const roots: Mailbox[] = [];

    for (const m of mailboxes) {
        const parent = m.parentId && present.has(m.parentId) ? m.parentId : null;
        if (parent) {
            const list = children.get(parent) ?? [];
            list.push(m);
            children.set(parent, list);
        } else {
            roots.push(m);
        }
    }

    // Cycles cannot happen through the API, but a corrupt store should not hang
    // the sidebar — `seen` bounds the walk whatever the data says.
    const seen = new Set<string>();
    const walk = (list: Mailbox[], depth: number): FolderNode[] => {
        const out: FolderNode[] = [];
        for (const mailbox of [...list].sort(compare)) {
            if (seen.has(mailbox.id)) continue;
            seen.add(mailbox.id);
            out.push({ mailbox, depth, children: walk(children.get(mailbox.id) ?? [], depth + 1) });
        }
        return out;
    };

    return walk(roots, 0);
}

/** Flattens the tree for rendering, skipping the children of collapsed nodes. */
export function flatten(nodes: FolderNode[], collapsed: Set<string>): FolderNode[] {
    const out: FolderNode[] = [];
    const visit = (list: FolderNode[]) => {
        for (const node of list) {
            out.push(node);
            if (!collapsed.has(node.mailbox.id)) visit(node.children);
        }
    };
    visit(nodes);
    return out;
}

/** Full path, e.g. `Projects/mail-core` — what Sieve's fileinto wants. */
export function pathOf(mailboxes: Mailbox[], id: string): string {
    const byId = new Map(mailboxes.map((m) => [m.id, m]));
    const parts: string[] = [];
    let current = byId.get(id);
    const guard = new Set<string>();
    while (current && !guard.has(current.id)) {
        guard.add(current.id);
        parts.unshift(current.name);
        current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    return parts.join('/');
}

/** True when `id` is `candidate` or sits underneath it — a folder cannot move into itself. */
export function isDescendant(mailboxes: Mailbox[], id: string, candidate: string): boolean {
    if (id === candidate) return true;
    const byId = new Map(mailboxes.map((m) => [m.id, m]));
    let current = byId.get(candidate);
    const guard = new Set<string>();
    while (current?.parentId && !guard.has(current.id)) {
        guard.add(current.id);
        if (current.parentId === id) return true;
        current = byId.get(current.parentId);
    }
    return false;
}

/**
 * What to show in the sidebar.
 *
 * A folder that carries a known role gets the reader's own word for it —
 * "Gelen Kutusu" rather than "Inbox" — because those names come from whichever
 * server or importer created the mailbox and are nobody's deliberate choice.
 * A folder someone named themselves is left exactly as they named it.
 */
export function displayLabel(mailbox: Mailbox, label: (role: Role) => string): string {
    const role = (mailbox.role as Role | null) ?? roleFromName(mailbox.name);
    if (!role) return mailbox.name;
    // Only rename what looks like a default. "Faturalar" with role archive is a
    // deliberate choice and must survive.
    return roleFromName(mailbox.name) === role ? label(role) : mailbox.name;
}
